import { Link } from "react-router-dom";
import { Github, Linkedin, Twitter, Rss, Send } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageToggle from "@/components/LanguageToggle";

const SOCIAL = [
  { href: "https://www.linkedin.com/", icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
  { href: "https://twitter.com/", icon: Twitter, label: "X / Twitter", testid: "social-twitter" },
  { href: "https://github.com/", icon: Github, label: "GitHub", testid: "social-github" },
  { href: "https://bsky.app/", icon: Rss, label: "Bluesky", testid: "social-bluesky" },
];

export default function Footer() {
  const { t } = useI18n();

  const exploreLinks = [
    { to: "/for-students", label: t("nav.forStudents") },
    { to: "/for-business", label: t("nav.forBusiness") },
    { to: "/pricing", label: t("nav.pricing") },
    { to: "/audit", label: t("nav.audit") },
  ];
  const siteLinks = [
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-charcoal text-brand-off mt-24"
    >
      <div className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-block w-2 h-2 rounded-full bg-brand-cyan" />
              <span className="font-semibold tracking-tight text-[15px]">Toms Clarifies</span>
            </div>
            <p className="text-white/70 text-[15px] leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
            <a
              href="mailto:makesense@tomsclarifies.com"
              data-testid="footer-email"
              className="mt-6 inline-flex items-center gap-2 text-[14px] text-white/85 hover:text-brand-cyan transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              makesense@tomsclarifies.com
            </a>
            <div className="mt-6">
              <LanguageToggle variant="light" />
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow-light mb-4">{t("footer.explore")}</div>
            <ul className="space-y-2.5">
              {exploreLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-explore-${link.to.replace(/\//g, "")}`}
                    className="text-[14.5px] text-white/75 hover:text-brand-off transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="eyebrow-light mb-4">{t("footer.site")}</div>
            <ul className="space-y-2.5">
              {siteLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    data-testid={`footer-site-${link.to.replace(/\//g, "")}`}
                    className="text-[14.5px] text-white/75 hover:text-brand-off transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow-light mb-4">{t("footer.findMe")}</div>
            <div className="flex flex-wrap gap-2">
              {SOCIAL.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={s.testid}
                  aria-label={s.label}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            <p className="mt-6 text-white/50 text-[13px]">{t("footer.basedIn")}</p>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <p className="text-[12.5px] text-white/45 tracking-wide">
            © {new Date().getFullYear()} Toms Reksna. {t("footer.copyrightSuffix")}
          </p>
          <p className="text-[12.5px] text-white/45">{t("footer.builtWith")}</p>
        </div>
      </div>
    </footer>
  );
}
